const MAX_UPLOAD_WIDTH = 1920;
const MAX_UPLOAD_HEIGHT = 1440;
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const UPLOAD_JPEG_QUALITY = 0.85;
const MIN_UPLOAD_JPEG_QUALITY = 0.55;
const MAX_OPTIMIZATION_ATTEMPTS = 6;

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export async function readImageAsDataUrl(
  source: File | Blob,
): Promise<string> {
  return toDataUrl(source);
}

export async function prepareImageForUpload(
  file: File,
  preservePng = file.type === 'image/png',
): Promise<File> {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Select a JPEG, PNG, or WebP image.');
  }

  const dataUrl = await toDataUrl(file);
  const image = await loadImage(dataUrl, 'Image could not be decoded.');

  let scale = Math.min(
    1,
    MAX_UPLOAD_WIDTH / image.width,
    MAX_UPLOAD_HEIGHT / image.height,
  );
  let quality = UPLOAD_JPEG_QUALITY;

  for (let attempt = 0; attempt < MAX_OPTIMIZATION_ATTEMPTS; attempt += 1) {
    const blob = await drawImageToBlob(
      image,
      Math.max(1, Math.round(image.width * scale)),
      Math.max(1, Math.round(image.height * scale)),
      preservePng ? 'image/png' : 'image/jpeg',
      preservePng ? undefined : quality,
    );

    if (blob.size <= MAX_UPLOAD_BYTES) {
      const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
      const extension = preservePng ? 'png' : 'jpg';

      return new File(
        [blob],
        `${baseName}.${extension}`,
        {
          type: blob.type,
          lastModified: file.lastModified,
        },
      );
    }

    if (!preservePng && quality > MIN_UPLOAD_JPEG_QUALITY) {
      quality = Math.max(
        MIN_UPLOAD_JPEG_QUALITY,
        quality - 0.1,
      );
    } else {
      scale *= 0.75;
    }
  }

  throw new Error(
    'Image could not be optimized within the 5 MB upload limit.',
  );
}

export async function resizeImageForUpload(
  source: File | Blob | string,
  preservePng = false,
): Promise<Blob> {
  const dataUrl = await toDataUrl(source);
  const img = await loadImage(dataUrl, 'Image load failed');

  const { width, height } = img;

  // Skip resize if already within limits
  if (width <= MAX_UPLOAD_WIDTH && height <= MAX_UPLOAD_HEIGHT) {
    if (typeof source === 'string') {
      return dataUrlToBlob(dataUrl);
    }

    return source;
  }

  const scale = Math.min(
    MAX_UPLOAD_WIDTH / width,
    MAX_UPLOAD_HEIGHT / height,
  );
  const targetW = Math.round(width * scale);
  const targetH = Math.round(height * scale);

  // Preserve PNG for signatures (line art); use JPEG for photos
  return drawImageToBlob(
    img,
    targetW,
    targetH,
    preservePng ? 'image/png' : 'image/jpeg',
    preservePng ? undefined : UPLOAD_JPEG_QUALITY,
  );
}

function loadImage(
  src: string,
  errorMessage: string,
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(errorMessage));
    image.src = src;
  });
}

function drawImageToBlob(
  image: HTMLImageElement,
  width: number,
  height: number,
  type: string,
  quality?: number,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    return Promise.reject(
      new Error('Canvas context unavailable'),
    );
  }

  context.drawImage(image, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob failed'));
      },
      type,
      quality,
    );
  });
}

async function toDataUrl(
  source: File | Blob | string,
): Promise<string> {
  if (typeof source === 'string') {
    return source;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(source);
  });
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: mime });
}
